(() => {
  if (customElements.get('promo-popup')) return;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const CLOSE_ANIMATION_FALLBACK_MS = 300;
  const LOCK_CLASS = 'promo-popup-lock';

  const storage = {
    get(store, key) {
      try {
        return window[store].getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(store, key, value) {
      try {
        window[store].setItem(key, value);
      } catch (error) {
        // Storage unavailable (private mode, blocked cookies): fail silently.
      }
    },
  };

  /** Parses "YYYY-MM-DD" as local midnight. Returns null when empty or invalid. */
  const parseLocalDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
    if (!match) return null;
    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day);
    return date.getMonth() === month - 1 ? date : null;
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Department (region) detection -------------------------------------
  const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json';
  const GEO_TIMEOUT_MS = 4000;
  const GEO_CACHE_KEY = 'promo-popup:geo-region';
  const GEO_NONE = 'none';

  // Normalized department name -> ISO 3166-2:PE code.
  const DEPARTMENTS = {
    amazonas: 'PE-AMA',
    ancash: 'PE-ANC',
    apurimac: 'PE-APU',
    arequipa: 'PE-ARE',
    ayacucho: 'PE-AYA',
    cajamarca: 'PE-CAJ',
    callao: 'PE-CAL',
    cusco: 'PE-CUS',
    cuzco: 'PE-CUS',
    huancavelica: 'PE-HUV',
    huanuco: 'PE-HUC',
    ica: 'PE-ICA',
    junin: 'PE-JUN',
    'la libertad': 'PE-LAL',
    lambayeque: 'PE-LAM',
    lima: 'PE-LIM',
    loreto: 'PE-LOR',
    'madre de dios': 'PE-MDD',
    moquegua: 'PE-MOQ',
    pasco: 'PE-PAS',
    piura: 'PE-PIU',
    puno: 'PE-PUN',
    'san martin': 'PE-SAM',
    tacna: 'PE-TAC',
    tumbes: 'PE-TUM',
    ucayali: 'PE-UCA',
  };

  /** "Provincia Constitucional del Callao" -> "callao", "Lima Province" -> "lima". */
  const normalizeRegionName = (name) =>
    (name || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\b(region|province|provincia|departamento|department|constitutional|constitucional)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^((of the|of|del|de|el)\s+)+/, '')
      .trim();

  /** Resolves the visitor's department code via IP, or null. Cached per session. */
  const detectRegionByIp = async () => {
    const cached = storage.get('sessionStorage', GEO_CACHE_KEY);
    if (cached) return cached === GEO_NONE ? null : cached;

    let region = null;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
    try {
      const response = await fetch(GEO_ENDPOINT, { signal: controller.signal, credentials: 'omit' });
      if (response.ok) {
        const geo = await response.json();
        if (geo.country_code === 'PE') region = DEPARTMENTS[normalizeRegionName(geo.region)] || null;
      }
    } catch (error) {
      // Blocked, offline or timed out: treated as "unknown department".
    } finally {
      window.clearTimeout(timeout);
    }

    storage.set('sessionStorage', GEO_CACHE_KEY, region || GEO_NONE);
    return region;
  };

  class PromoPopup extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.regionCheck = Promise.resolve(false);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.image = this.dialog.querySelector('.promo-popup__image');
      this.storageKey = this.dataset.storageKey;

      this.dialog.querySelector('[data-promo-popup-close]')?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (event) => {
        // Clicks on the backdrop target the <dialog> itself.
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove(LOCK_CLASS));

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        return;
      }

      if (this.isEligible()) {
        // Resolve the department while the delay runs, so opening is not slowed down.
        this.regionCheck = this.isRegionAllowed();
        this.timer = window.setTimeout(() => this.openWhenVisible(), Number(this.dataset.delay) || 0);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.documentElement.classList.remove(LOCK_CLASS);
    }

    isEligible() {
      const now = new Date();
      const start = parseLocalDate(this.dataset.startDate);
      const end = parseLocalDate(this.dataset.endDate);
      if (start && now < start) return false;
      if (end && now >= new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)) return false;

      switch (this.dataset.frequency) {
        case 'session':
          return !storage.get('sessionStorage', this.storageKey);
        case 'days': {
          const lastShown = Number(storage.get('localStorage', this.storageKey));
          const days = Math.max(1, Number(this.dataset.frequencyDays) || 1);
          return !lastShown || Date.now() - lastShown >= days * DAY_MS;
        }
        default:
          return true;
      }
    }

    async isRegionAllowed() {
      const allowed = this.dataset.regions || '';
      if (allowed === 'all') return true;

      const regions = allowed.split(',').filter(Boolean);
      if (!regions.length) return false;

      const region = this.dataset.customerRegion || (await detectRegionByIp());
      return Boolean(region) && regions.includes(region);
    }

    recordShown() {
      if (this.dataset.frequency === 'session') storage.set('sessionStorage', this.storageKey, '1');
      if (this.dataset.frequency === 'days') storage.set('localStorage', this.storageKey, String(Date.now()));
    }

    /** Avoid opening in a background tab; wait until the visitor comes back. */
    openWhenVisible() {
      if (document.visibilityState === 'visible') {
        this.open();
      } else {
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }
    }

    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.open();
    }

    async open({ track = true } = {}) {
      if (this.dialog.open) return;
      // Never stack on top of another modal (cart drawer, newsletter, age verifier...).
      if (track && !(await this.regionCheck)) return;
      if (track && document.querySelector('dialog[open]')) return;

      if (this.image) {
        this.image.loading = 'eager';
        await this.image.decode().catch(() => {});
      }
      if (!this.isConnected || this.dialog.open) return;

      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      document.documentElement.classList.add(LOCK_CLASS);
      if (track) this.recordShown();
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;

      if (prefersReducedMotion()) {
        this.dialog.close();
        return;
      }

      const finish = () => {
        window.clearTimeout(fallback);
        this.dialog.classList.remove('is-closing');
        if (this.dialog.open) this.dialog.close();
      };
      const fallback = window.setTimeout(finish, CLOSE_ANIMATION_FALLBACK_MS);
      this.dialog.addEventListener('animationend', finish, { once: true });
      this.dialog.classList.add('is-closing');
    }

    onSectionSelect(event) {
      if (event.detail.sectionId === this.dataset.sectionId) this.open({ track: false });
    }

    onSectionDeselect(event) {
      if (event.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close();
    }
  }

  customElements.define('promo-popup', PromoPopup);
})();
